/**
 * Getting Started checklist — reflects real onboarding state for the EDR
 * connector. Each step links to the relevant area of the console.
 */
import { Link } from "react-router-dom";
import { Check, Circle, ArrowRight } from "lucide-react";
import { PageHeader } from "@/components/ui/page-header";
import { Card, CardContent } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { useConnector } from "@/hooks/connectors/useConnector";
import { useEdrAlerts, useEdrHealthInsights } from "@/hooks/security/useEdrData";

interface Step {
  title: string;
  description: string;
  done: boolean;
  cta: { label: string; to: string };
}

export default function GettingStartedPage() {
  const edr = useConnector("edr");
  const alerts = useEdrAlerts({ limit: 1 });
  const health = useEdrHealthInsights();

  const sensors = (health.data?.sensorsHealthy ?? 0) + (health.data?.sensorsStale ?? 0);

  const steps: Step[] = [
    {
      title: "Connect the EDR connector",
      description: "Enable the Smplify EDR connector so the console can talk to your sensor fleet.",
      done: edr.status === "enabled",
      cta: { label: "Open connectors", to: "/settings/connectors/edr" },
    },
    {
      title: "Enrol your first sensor",
      description: "Deploy the ESF sensor via MDM. Once it checks in, it shows up here.",
      done: sensors > 0,
      cta: { label: "View sensors", to: "/security/devices" },
    },
    {
      title: "Review your first alert",
      description: "Walk through the alert detail drawer and explore response actions.",
      done: (alerts.data?.length ?? 0) > 0,
      cta: { label: "Open alerts", to: "/security/alerts" },
    },
    {
      title: "Configure SIEM forwarding",
      description: "Stream EDR events to your SIEM of choice. Optional but recommended.",
      done: false,
      cta: { label: "Set up forwarding", to: "/system/siem" },
    },
  ];

  const done = steps.filter((s) => s.done).length;

  return (
    <div className="px-6 py-6 space-y-6">
      <PageHeader
        title="Getting started"
        description={`Spin up Smplify EDR in four steps. ${done} of ${steps.length} complete.`}
      />

      <div className="grid gap-3">
        {steps.map((s, i) => (
          <Card key={s.title}>
            <CardContent className="flex items-center gap-4 p-4">
              <div
                className={`flex h-8 w-8 shrink-0 items-center justify-center rounded-full border ${
                  s.done
                    ? "border-success/40 bg-success/10 text-success"
                    : "border-border bg-muted text-muted-foreground"
                }`}
                aria-hidden
              >
                {s.done ? <Check className="h-4 w-4" /> : <Circle className="h-3 w-3" />}
              </div>
              <div className="flex-1 min-w-0">
                <div className="flex items-baseline gap-2">
                  <span className="text-xs font-medium uppercase tracking-wider text-muted-foreground">
                    Step {i + 1}
                  </span>
                  <span className="text-sm font-semibold text-foreground">{s.title}</span>
                </div>
                <p className="mt-0.5 text-xs text-muted-foreground">{s.description}</p>
              </div>
              <Button asChild variant={s.done ? "ghost" : "outline"} size="sm">
                <Link to={s.cta.to}>
                  {s.cta.label} <ArrowRight className="ml-1 h-3 w-3" />
                </Link>
              </Button>
            </CardContent>
          </Card>
        ))}
      </div>
    </div>
  );
}
